import {useDispatch, useSelector} from 'react-redux'
import Overview from '../../assets/icons/Overview'
import {Badge, Box, Button, Paper, Stack} from '@mui/material'
import commonActions from '../../actions/common.actions'
import {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {subscriptionTags} from '../../assets/constants'
import {useLocation} from 'react-router-dom'
import Holdings from '../../assets/icons/Holdings'
import websocketActions from '../../actions/websocket.actions'
import Trade from '../../assets/icons/Trade'
import NewsIcon from '../../assets/icons/NewsIcon'
import SecurityOverview from '../securityInfo/SecurityOverview'
import ScreenerSecurityOverview from '../securityInfo/ScreenerSecurityOverview'
import {WidgetListItems} from '../../util/sidebar'
import Positions from '../../assets/icons/Positions'
import {getNoOfOpenOrders, getNoOfPositions} from '../../selectors/orders'
import Watchlist from '../../assets/icons/watchlist'
import AlarmIcon from '../../assets/icons/AlarmIcon'
import {NonInteractiveTooltip} from '../custom'
import {sideBarBottomIconStackSx} from './sideBarBottomIconButtonSx'
import ROUTES from '../../assets/routes'
import {matchRoute} from '../../util/routes'
import {HeadsetTabIcon} from '../../assets/icons/HeadsetIcon'
import {Calendars} from '../calendars/Calendars'
import {selectRightSidebarWidget} from '../../selectors/common'
import { GlobalNotificationDrawer } from '../GlobalNotification/GlobalNotificationDrawer'
import { isWidgetbarLayoutWidget } from '../../util/sidebar'
import QuickWidgetsMenu from '../quickWidgetsMenu/quickWidgetsMenu'
import { WIDGETBAR_VIEW_ELEMENTS } from './widgetbarViews'
import WidgetbarPagesPanel from './WidgetbarPagesPanel'
import AiSparkIcon from '../../assets/icons/AiSparkIcon'
import { selectUserDetails } from '../../selectors/user'
import { shouldShowAiAssistantInSidebar } from '../aiAssistant/aiAssistantConfig'

const AI_SIDEBAR_BUTTON = {
    element: <AiSparkIcon />,
    label: 'AI Assistant',
    code: WidgetListItems.AI_ASSISTANT,
} as const

const buttons = [
    {
        element: <Overview/>,
        label: 'Overview',
        code: WidgetListItems.OVERVIEW
    },
    {
        element: <Watchlist/>,
        label: 'Watchlist',
        code: WidgetListItems.WATCHLIST
    },
    {
        element: <AlarmIcon/>,
        label: 'Alert',
        code: WidgetListItems.ALERT
    },
    {
        element: <NewsIcon/>,
        label: 'News',
        code: WidgetListItems.MARKET_NEWS
    },
    {
        element: <Trade style={{fontSize: "40px"}}/>,
        label: 'Trade',
        code: WidgetListItems.TRADE
    },
    {
        element: <Positions/>,
        label: 'Positions/Orders',
        code: WidgetListItems.POSITIONS
    },
    // {
    //     element: <OrderIcon color="inherit" style={{fontSize: '40px'}}/>,
    //     label: 'Orders',
    //     code: WidgetListItems.ORDERS
    // },
    {
        element: <Holdings/>,
        label: 'Holdings',
        code: WidgetListItems.HOLDINGS
    },
    {
        element: <HeadsetTabIcon/>,
        label: 'Help & feedback',
        code: WidgetListItems.SUPPORT
    }
]

const SideBarRight = ({blur}) => {
    const dispatch = useDispatch()
    const view = useSelector(selectRightSidebarWidget)
    const [rightSideWidgetView, setRightSideWidgetView] = useState<string | null>(view)
    const location = useLocation()
    
    const noOfOpenPositions = useSelector(getNoOfPositions)
    const noOfOpenOrders = useSelector(getNoOfOpenOrders)
    const userDetails = useSelector(selectUserDetails)
    const showAiAssistant = shouldShowAiAssistantInSidebar(userDetails)
    const sidebarButtons = useMemo(() => {
        if (!showAiAssistant) return buttons
        return [AI_SIDEBAR_BUTTON, ...buttons]
    }, [showAiAssistant])
    const isScreenerRoute = useMemo(
        () =>
            !!matchRoute(location.pathname, [
                { path: ROUTES.screener, key: 'screener' },
                { path: ROUTES.industries, key: 'industries' },
                { path: ROUTES.industriesLevel, key: 'industries-level' },
            ]),
        [location.pathname],
    )
    const overviewPanel = useMemo(
        () => (isScreenerRoute ? <ScreenerSecurityOverview /> : <SecurityOverview />),
        [isScreenerRoute]
    )

    const handleRightSideWidgetIconClick = useCallback((btnCode: WidgetListItems | null) => {
        const prevValue = rightSideWidgetView
        if (rightSideWidgetView === btnCode) {
            setRightSideWidgetView(null)
            commonActions.dispatchRightSideWidget(dispatch, null)
        } else {
            setRightSideWidgetView(btnCode)
            commonActions.dispatchRightSideWidget(dispatch, btnCode)
        }
        if (prevValue === WidgetListItems.OVERVIEW) {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                securitySnapshotSubscriptions: [
                    {
                        tag: subscriptionTags.INDEX_COMPONENT,
                        securities: []
                    }
                ]
            })
        }
        if (prevValue === WidgetListItems.WATCHLIST) {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                securitySnapshotSubscriptions: [
                    {
                        tag: subscriptionTags.WATCHLIST,
                        securities: []
                    }
                ]
            })
        }
    }, [dispatch, rightSideWidgetView])

    useEffect(() => {
        setRightSideWidgetView(view)
    }, [view])

    const activeWidgetContent = useMemo(() => {
        if (!isWidgetbarLayoutWidget(rightSideWidgetView)) return null
        if (rightSideWidgetView === WidgetListItems.OVERVIEW) {
            return overviewPanel
        }
        return WIDGETBAR_VIEW_ELEMENTS[rightSideWidgetView] ?? null
    }, [rightSideWidgetView, overviewPanel])

    const showWidgetbarPanel = isWidgetbarLayoutWidget(rightSideWidgetView)

    return (
        <>
            {
                showWidgetbarPanel ?
                    <WidgetbarPagesPanel blur={blur}>{activeWidgetContent}</WidgetbarPagesPanel>
                    : null
            }
            <Paper
                sx={{
                    gridArea: 'right-sidebar',
                    zIndex: 999,
                    borderRadius: '4px 4px 0 4px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    overflowY: 'auto',
                    marginLeft: 'var(--extra-small-spacing)',
                    minHeight: 0,
                    WebkitOverflowScrolling: 'touch'
                }}
                style={{ ...(blur ? { filter: 'blur(4px)', pointerEvents: 'none' } : {}) }}
            >
                <Box
                    id="side-bar-right-options"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minHeight: 0,
                        flex: '1 1 auto',
                        overflowY: 'auto',
                        // Breathing room between the last tab and the utilities below it.
                        pb: 1.5
                    }}
                >
                    {
                        sidebarButtons.map(
                            (btn, i) =>
                                <NonInteractiveTooltip arrow key={i} placement={'left'} title={btn.label}>
                                    <Button sx={{
                                        borderRadius: 0,
                                        maxHeight: 'screen',
                                        minWidth: 'max-content',
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        '&:hover': {
                                            backgroundColor: (theme) => (rightSideWidgetView === btn.code) ? theme.palette.background.default : null
                                        },
                                        backgroundColor: (theme) => (rightSideWidgetView === btn.code) ?
                                            theme.palette.background.default : null
                                    }}
                                        color={'inherit'}
                                        onClick={() => handleRightSideWidgetIconClick(btn.code as any)}
                                        key={i}
                                        data-event={`${btn.label} Icon Clicked`}
                                        data-event-action={rightSideWidgetView === btn.code ? 'Closed' : 'Opened'}
                                    >
                                        <Badge
                                            color="primary"
                                            sx={{
                                                display: 'inline-flex',
                                                flexDirection: 'column',
                                                alignItems: 'center'
                                            }}
                                            badgeContent={
                                                WidgetListItems.POSITIONS === btn.code ? noOfOpenPositions :
                                                    WidgetListItems.ORDERS === btn.code ? noOfOpenOrders : 0
                                            }
                                            overlap="circular"
                                        >
                                            {btn.element}
                                            {/*<Typography style={{ paddingBottom: '8px' }} variant="body2">*/}
                                            {/*    {btn.code}*/}
                                            {/*</Typography>*/}
                                        </Badge>
                                    </Button>
                                </NonInteractiveTooltip>
                        )
                    }

                </Box>
                {/* Bottom inset matches the left rail's profile icon: its 24px avatar sits in a 56px row plus
                    8px list padding (24px to the edge); the 28px icon in a 40px button leaves 6px, so 18px here. */}
                <Stack alignItems={'center'} sx={{ width: '100%', flexShrink: 0, pb: '18px', ...sideBarBottomIconStackSx }}>
                    <QuickWidgetsMenu />
                    <GlobalNotificationDrawer />
                    <Calendars/>
                </Stack>
            </Paper>
        </>
    )
}

export default memo(SideBarRight)